import { useQueryClient } from '@tanstack/react-query';
import useMutationApi from 'src/api/useMutationApi';

interface UpdateRebateAgreementRequest {
  status?: 'draft' | 'finalized';
  name?: string;
  startDate?: string;
  endDate?: string;
  notes?: string | null;
  rebatePaymentTerms?: string | null;
  purchasePaymentTerms?: string | null;
  isHistorical?: boolean;
}

interface InvoiceRemovalResult {
  removedInvoices: number;
  removedLineItems: number;
  removedDocuments: number;
  removedAssociations: number;
  warnings: string[];
}

interface UpdateRebateAgreementResponse {
  id: string;
  status: 'draft' | 'finalized';
  name: string;
  startDate: string;
  endDate: string;
  notes: string | null;
  isHistorical: boolean;
  invoiceRemovalResult?: InvoiceRemovalResult | null;
}

const useUpdateRebateAgreement = (agreementId: string) => {
  const queryClient = useQueryClient();

  const mutation = useMutationApi<
    UpdateRebateAgreementRequest,
    UpdateRebateAgreementResponse
  >({
    endpoint: `rebates/agreements/${agreementId}`,
    method: 'PUT',
    options: {
      onSuccess: () => {
        // Invalidate related queries to refresh the data
        queryClient.invalidateQueries({
          queryKey: ['rebateAgreement', agreementId],
        });
        queryClient.invalidateQueries({
          queryKey: ['rebateAgreements'],
        });
      },
    },
    successMessage: 'Agreement updated successfully',
  });

  // Return the mutation with a modified mutateAsync that unwraps the response
  return {
    ...mutation,
    mutateAsync: async (data: UpdateRebateAgreementRequest) => {
      const response = await mutation.mutateAsync(data);
      return response.data; // Unwrap and return only the data
    },
  };
};

// Keep the old hook name for backward compatibility (can be removed later)
export const useUpdateRebateAgreementStatus = useUpdateRebateAgreement;
export default useUpdateRebateAgreement;
